import { matchPath } from 'react-router-dom';

import type { RoutePathDefinition } from '@/types/routes';

/** URL is under this submenu (prefix match — works for nested / deep paths). */
function normalizeRoutePattern(path: string): string {
  const withLeading = path.startsWith('/') ? path : `/${path}`;
  const trimmed = withLeading.replace(/\/+$/, '');
  return trimmed === '' ? '/' : trimmed;
}

function routeMatches(path: string, pathname: string): boolean {
  return (
    matchPath(
      {
        path: normalizeRoutePattern(path),
        end: false,
      },
      pathname
    ) !== null
  );
}

/**
 * Whether a route's own `path` can be prefix-matched against a full pathname.
 * Nested leaf routes use paths relative to their parent — an empty/index path
 * ('') or a param-only path (':issue') would normalize to '/' or '/:issue' and,
 * with end:false, match essentially every URL, lighting up the parent submenu
 * everywhere. Their coverage is already provided by the parent section path
 * (also matched with end:false), so they are skipped here.
 */
function isMatchablePath(path: string | undefined): path is string {
  return typeof path === 'string' && path !== '' && !path.startsWith(':');
}

export function isRouteUnderSubmenu(item: RoutePathDefinition, pathname: string): boolean {
  if (isMatchablePath(item.path) && routeMatches(item.path, pathname)) {
    return true;
  }

  return (item.children ?? []).some((child) => isRouteUnderSubmenu(child, pathname));
}

/** `path` of the top-level `isSubMenu` route that owns the current URL, if any. */
export function findActiveTopLevelSubmenuPath(
  routes: RoutePathDefinition[],
  pathname: string
): string | null {
  const hit = routes.find((r) =>
    Boolean(r.isSubMenu && r.path && isRouteUnderSubmenu(r, pathname))
  );
  return hit?.path ?? null;
}
